// enci.form.js
import $ from 'jquery';
import 'bootstrap-validator';
import 'devextreme/ui/form';
import 'devextreme/ui/text_area';
import { debugLog } from './enci.log.js';
import { createMinioDataSource } from './enci.minioUtils.js';

/**
 * Extends jQuery to check if a selector matches any elements.
 * @returns {boolean} True if the selector matches at least one element, false otherwise.
 */
$.fn.exists = function () {
  return this.length > 0;
};

/**
 * Gets a jQuery object for a form selector.
 * @param {string} sel - The selector for the form
 * @returns {jQuery} A jQuery object representing the form
 */
export const getForm = sel => $(`${sel}`);

/**
 * Gets the DevExtreme form instance for a given selector.
 * @param {string} sel - The selector for the form
 * @returns {Object} The DevExtreme form instance
 */
export const getFormInstance = sel => getForm(sel).dxForm('instance');

/**
 * Generates a configuration object for form buttons.
 * @returns {FormButtons} The form buttons configuration
 */
export const formButtons = () => ({
  itemType: 'group',
  cssClass: 'last-group',
  items: [
    {
      itemType: 'group',
      cssClass: 'buttons-group',
      colCountByScreen: { xs: 2, sm: 2, md: 2, lg: 2 },
      items: [
        {
          itemType: 'button',
          name: 'Preview',
          buttonOptions: {
            text: 'Preview',
            type: 'default',
            stylingMode: 'outlined',
            useSubmitBehavior: false,
            width: '120px',
          },
        },
        {
          itemType: 'button',
          name: 'Update',
          buttonOptions: {
            text: 'Update',
            type: 'default',
            useSubmitBehavior: true,
            width: '120px',
          },
        },
      ],
    },
  ],
});

/**
 * Generates configuration options for a Minio select box.
 * @returns {MinioSelectBoxOptions} The Minio select box configuration options
 */
export const formMinioSelectBox = (
  initialBucket = '',
  initialPrefix = '',
  onValueChangedCallback
) => {
  let currentBucket = initialBucket;
  let currentPrefix = initialPrefix;

  return {
    displayExpr: item => {
      if (item && item.size) {
        const sizeInKilobytes = (item.size / 1024).toFixed(1);
        return `${item.key} (${sizeInKilobytes} KB)`;
      }
      return item ? item.key : '';
    },
    valueExpr: 'key',
    inputAttr: { 'aria-label': 'Simple Image' },
    onValueChanged: e => {
      debugLog('formMinioSelectBox >> onValueChanged:', { obj: e });
      if (typeof onValueChangedCallback === 'function') {
        onValueChangedCallback(e.value);
      }
    },
    onOpened: e => {
      debugLog('formMinioSelectBox >> onOpened:', { obj: e });
      e.component.option('dataSource', createMinioDataSource(currentBucket, currentPrefix));
    },
    onContentReady: e => {
      const selectBox = e.component;
      // Expose methods to update bucket and prefix
      selectBox.updateMinioContext = async (bucket, prefix) => { // , newValue
        currentBucket = bucket;
        currentPrefix = prefix;
        const dataSource = createMinioDataSource(currentBucket, currentPrefix);
        await dataSource.load();
      };
    },
  };
};

/**
 * Initializes phone input formatting for elements with the class '.phone-input'.
 */
export async function initializeFormatPhoneInput() {
  debugLog('initializeFormatPhoneInput');

  const phoneInputs = document.querySelectorAll('.phone-input');

  if (!phoneInputs.length) {
    debugLog('initializeFormatPhoneInput >> No phone inputs found.', { type: 'error' });
    return;
  }

  phoneInputs.forEach(phoneInput => {
    phoneInput.addEventListener('input', e => {
      const input = e.target.value.replace(/\D/g, '').substring(0, 10);
      const formattedInput = formatPhoneNumber(input);

      if (formattedInput !== e.target.value) {
        e.target.value = formattedInput;
      }
    });
  });
}

/**
 * Formats a phone number string.
 * @param {string} input - The input string to format
 * @returns {string} The formatted phone number
 */
function formatPhoneNumber(input) {
  if (input.length > 6) {
    return `(${input.substring(0, 3)}) ${input.substring(3, 6)}-${input.substring(6)}`;
  } else if (input.length > 3) {
    return `(${input.substring(0, 3)}) ${input.substring(3)}`;
  } else if (input.length > 0) {
    return `(${input}`;
  }
  return '';
}

/**
 * Validates and handles the submission of contact forms using AJAX.
 */
export async function validateContactForm() {
  debugLog('validateContactForm');

  const forms = $('#contact-form, #queto-form');

  if (!forms.exists()) {
    debugLog('validateContactForm >> No forms to validate.', { type: 'error' });
    return;
  }

  forms.validator();

  forms.on('submit', handleFormSubmit);

  $('.contact-btn').on('click', toggleContactForm);
  $('.close-btn').on('click', closeContactForm);
}

/**
 * Handles form submission.
 * @param {Event} e - The submit event
 */
function handleFormSubmit(e) {
  if (!e.isDefaultPrevented()) {
    e.preventDefault();
    const url = 'php/contact.php';
    $.ajax({
      type: 'POST',
      url: url,
      data: $(e.target).serialize(),
      success: data => handleFormSubmitSuccess(data, e.target),
    });
  }
}

/**
 * Handles successful form submission.
 * @param {Object} data - The response data
 * @param {HTMLFormElement} form - The form element
 */
function handleFormSubmitSuccess(data, form) {
  const messageAlert = `alert-${data.type}`;
  const messageText = data.message;
  const alertBox = `<div class="alert ${messageAlert} alert-dismissable"><button type="button" class="close" data-dismiss="alert" aria-hidden="true">&times;</button>${messageText}</div>`;
  $(form).find('.messages').html(alertBox).show().delay(2000).fadeOut('slow');
  form.reset();
}

/**
 * Toggles the contact form visibility.
 */
function toggleContactForm() {
  $(this).toggleClass('active');
  $('.contact-form').animate({
    right: $(this).hasClass('active') ? '0' : '-450px',
  });
}

/**
 * Closes the contact form.
 */
function closeContactForm() {
  $('.contact-form').animate({ right: '-450px' });
}

/**
 * Class for creating and managing forms.
 */
export class FormCreator {
  constructor() {
    this.plugins = [];
  }

  /**
   * @param {Object} plugin - Plugin object with init and cleanup methods
   */
  addPlugin(plugin) {
    if (typeof plugin.init === 'function' && typeof plugin.cleanup === 'function') {
      this.plugins.push(plugin);
    } else {
      console.warn('Invalid plugin structure. Plugins must have init and cleanup methods.');
    }
  }

  /**
   * @param {Object} options - Form configuration options
   * @returns {Promise<Object>} - Promise resolving to the form instance
   */
  async loadForm(config) {
    if (!config.selector || !$(config.selector).exists()) {
      throw new Error('Invalid selector or element not found');
    }

    try {
      const formOptions = {
        ...this._getDefaultOptions(),
        ...config,
      };

      // Initialize plugins
      for (const plugin of this.plugins) {
        await plugin.init(formOptions);
      }

      const formInstance = $(config.selector).dxForm(formOptions).dxForm('instance');

      if (typeof config.onSubmit === 'function') {
        this._bindSubmitHandler(
          config.formContainer,
          formInstance,
          config.onSubmit,
          config.gridWidget
        );
      }

      // Add cleanup method
      formInstance.cleanup = () => {
        for (const plugin of this.plugins) {
          plugin.cleanup(formInstance);
        }
        formInstance.dispose();
      };

      return formInstance;
    } catch (error) {
      console.error('Error loading form:', error);
      throw error;
    }
  }

  _getDefaultOptions() {
    return {
      labelMode: 'outside',
      showColonAfterLabel: true,
      // ... other default options
    };
  }

  /**
   * Binds a submit handler to the form.
   * @param {string} formContainer - The form container selector
   * @param {Object} formInstance - The form instance
   * @param {Function} onSubmit - The submit handler
   * @param {Object} gridWidget - The grid widget (if any)
   */
  _bindSubmitHandler(formContainer, formInstance, onSubmit, gridWidget) {
    $(formContainer).on('submit', async e => {
      e.preventDefault();
      const updateButton = formInstance.getButton('Update');
      updateButton.option('disabled', true);

      try {
        await onSubmit(e, formInstance);
        if (gridWidget) {
          gridWidget.refresh();
        }
      } catch (error) {
        console.error('Error during form submission:', error);
      } finally {
        updateButton.option('disabled', false);
      }
    });
  }
}

// Export a singleton instance
export const formCreator = new FormCreator();